import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface IndustryContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  primaryCta: string
  secondaryCta: string
  badges: string[]
  pillarsEyebrow: string
  pillarsTitle: string
  pillarsBody: string
  pillars: Pillar[]
  howEyebrow: string
  howTitle: string
  steps: Step[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', IndustryContent> = {
  en: {
    metaTitle: 'Identity & access for manufacturing',
    metaDescription:
      'IT/OT identity in one platform. Shop-floor friendly sign-in, vendor PAM, and IEC 62443-aligned governance for plants and supply chains.',
    heroEyebrow: 'Industries · Manufacturing',
    heroTitle: ['IT and OT,', 'one identity fabric.'],
    heroBody:
      'Plant-floor operators, control-system engineers, and corporate users on one identity platform. Brokered vendor access, IEC 62443-aligned governance, and shop-floor-friendly sign-in.',
    primaryCta: 'Start free trial →',
    secondaryCta: 'Talk with our experts',
    badges: [
      'IEC 62443-aligned',
      'OT-friendly authentication',
      'Brokered vendor access',
    ],
    pillarsEyebrow: 'What you get',
    pillarsTitle: 'What manufacturers get with Monofor.',
    pillarsBody:
      'Identity is one of the few control planes that spans IT and OT cleanly. Monofor uses that fact to give plant operators, engineers, and auditors one consistent picture.',
    pillars: [
      {
        title: 'OT and IT identity, one platform',
        description:
          'Identity that bridges office workers, plant-floor operators, and the engineers who keep both running. One audit trail across IT and OT.',
        bullets: [
          'Workforce identity for office and engineering',
          'Operator sign-in on HMI and SCADA workstations',
          'PAM for control-system administrators',
          'Vendor access with hard expiry',
        ],
      },
      {
        title: 'Shop-floor friendly authentication',
        description:
          'Gloves, dust, no phone: the constraints are real. Monosign supports proximity cards, shared kiosks, and rapid role-switching at the workstation.',
        bullets: [
          'Proximity-card and PIN sign-in',
          'Shift-based shared-kiosk profiles',
          'Rapid role-switching at the HMI',
          'Offline OTP for disconnected lines',
        ],
      },
      {
        title: 'Privileged access for ICS / OT vendors',
        description:
          'Third-party integrators and vendors get brokered, recorded, time-bound access, not flat VPN connections into the OT network.',
        bullets: [
          'No flat VPN: gateway-brokered sessions only',
          'Recording with command-level transcription',
          'Per-vendor access window with auto-revoke',
          'Network segmentation-aware deployment',
        ],
      },
      {
        title: 'IEC 62443 and supply-chain ready',
        description:
          'Control-mapping to IEC 62443, NIST 800-82, and supply-chain attestation frameworks. Evidence on demand for OEM audits.',
        bullets: [
          'IEC 62443 control mapping',
          'Vendor-attestation packs',
          'Air-gapped deployment for sensitive zones',
          'Signed evidence exports',
        ],
      },
    ],
    howEyebrow: 'How it works',
    howTitle: 'Bridge. Broker. Attest.',
    steps: [
      {
        title: 'Bridge IT and OT identity',
        description:
          'Connect AD, MES, and ICS user stores. Monosign normalizes them so policy applies consistently across the boundary.',
      },
      {
        title: 'Lock down vendor access',
        description:
          'Route every integrator and OEM engineer through Monopam. Time-bound, recorded, and policy-enforced; no flat network connections.',
      },
      {
        title: 'Operate, observe, attest',
        description:
          'Watch for anomalous OT access. Export attestation packs for supply-chain audits. Sleep at night.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      {
        label: 'Monopam Gateway',
        description: 'Agentless brokered access for OT and ICS systems.',
        href: '/pam/gateway',
      },
      {
        label: 'Just-in-time access',
        description: 'Time-bound elevation for vendor and operator tasks.',
        href: '/pam/jit-access',
      },
      {
        label: 'External & partner access',
        description: 'Use case: vendor and integrator access without flat-network risk.',
        href: '/solutions/external-partners',
      },
    ],
  },
  tr: {
    metaTitle: 'Üretim sektörü için kimlik ve erişim',
    metaDescription:
      'Tek platformda IT/OT kimliği. Fabrikalar ve tedarik zincirleri için saha dostu giriş, tedarikçi PAM’i ve IEC 62443 ile uyumlu yönetişim.',
    heroEyebrow: 'Sektörler · Üretim',
    heroTitle: ['IT ve OT,', 'tek kimlik dokusu.'],
    heroBody:
      'Saha operatörleri, kontrol sistemi mühendisleri ve kurumsal kullanıcılar tek kimlik platformunda. Aracılı tedarikçi erişimi, IEC 62443 ile uyumlu yönetişim ve saha dostu oturum açma.',
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Uzmanlarımızla görüşün',
    badges: [
      'IEC 62443 ile uyumlu',
      'OT dostu kimlik doğrulama',
      'Aracılı tedarikçi erişimi',
    ],
    pillarsEyebrow: 'Neler elde edersiniz',
    pillarsTitle: 'Üreticilerin Monofor ile elde ettikleri.',
    pillarsBody:
      'Kimlik, IT ile OT’yi temiz biçimde kapsayan az sayıdaki kontrol düzleminden biridir. Monofor bu gerçeği kullanarak saha operatörlerine, mühendislere ve denetçilere tek tutarlı bir resim sunar.',
    pillars: [
      {
        title: 'OT ve IT kimliği, tek platform',
        description:
          'Ofis çalışanlarını, saha operatörlerini ve her ikisini ayakta tutan mühendisleri birleştiren kimlik. IT ve OT genelinde tek denetim izi.',
        bullets: [
          'Ofis ve mühendislik için çalışan kimliği',
          'HMI ve SCADA iş istasyonlarında operatör girişi',
          'Kontrol sistemi yöneticileri için PAM',
          'Kesin süre sonlu tedarikçi erişimi',
        ],
      },
      {
        title: 'Saha dostu kimlik doğrulama',
        description:
          'Eldiven, toz, telefon yok: kısıtlar gerçek. Monosign; yakınlık kartlarını, ortak kioskları ve iş istasyonunda hızlı rol değiştirmeyi destekler.',
        bullets: [
          'Yakınlık kartı ve PIN ile giriş',
          'Vardiya bazlı ortak kiosk profilleri',
          'HMI’da hızlı rol değiştirme',
          'Bağlantısız hatlar için çevrimdışı OTP',
        ],
      },
      {
        title: 'ICS / OT tedarikçileri için ayrıcalıklı erişim',
        description:
          'Üçüncü taraf entegratörler ve tedarikçiler; OT ağına düz VPN bağlantıları yerine aracılı, kayıtlı ve süreli erişim alır.',
        bullets: [
          'Düz VPN yok: yalnızca gateway aracılığıyla oturumlar',
          'Komut düzeyinde döküm ile kayıt',
          'Otomatik iptalli tedarikçi başına erişim penceresi',
          'Ağ segmentasyonuna duyarlı kurulum',
        ],
      },
      {
        title: 'IEC 62443 ve tedarik zincirine hazır',
        description:
          'IEC 62443, NIST 800-82 ve tedarik zinciri beyan çerçeveleriyle kontrol eşlemesi. OEM denetimleri için istendiği anda kanıt.',
        bullets: [
          'IEC 62443 kontrol eşlemesi',
          'Tedarikçi beyan paketleri',
          'Hassas bölgeler için hava boşluklu (air-gapped) kurulum',
          'İmzalı kanıt dışa aktarımları',
        ],
      },
    ],
    howEyebrow: 'Nasıl çalışır',
    howTitle: 'Köprü kurun. Aracılayın. Kanıtlayın.',
    steps: [
      {
        title: 'IT ve OT kimliği arasında köprü kurun',
        description:
          'AD, MES ve ICS kullanıcı depolarını bağlayın. Monosign bunları normalleştirir; politika sınırın iki yanında tutarlı biçimde uygulanır.',
      },
      {
        title: 'Tedarikçi erişimini güvenceye alın',
        description:
          'Her entegratörü ve OEM mühendisini Monopam üzerinden yönlendirin. Süreli, kayıtlı ve politikayla uygulanan erişim; düz ağ bağlantısı yok.',
      },
      {
        title: 'İşletin, izleyin, beyan edin',
        description:
          'Anormal OT erişimini izleyin. Tedarik zinciri denetimleri için beyan paketlerini dışa aktarın. Geceleri rahat uyuyun.',
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      {
        label: 'Monopam Gateway',
        description: 'OT ve ICS sistemleri için ajansız aracılı erişim.',
        href: '/pam/gateway',
      },
      {
        label: 'Tam zamanında erişim',
        description: 'Tedarikçi ve operatör görevleri için süreli yetki yükseltme.',
        href: '/pam/jit-access',
      },
      {
        label: 'Dış ve iş ortağı erişimi',
        description: 'Kullanım senaryosu: düz ağ riski olmadan tedarikçi ve entegratör erişimi.',
        href: '/solutions/external-partners',
      },
    ],
  },
}

type Props = { locale: string }

export default function ManufacturingPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/solutions/industries/manufacturing"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: '/contact' }}
          badges={c.badges}
        />
        <SolutionPillars
          eyebrow={c.pillarsEyebrow}
          title={<>{c.pillarsTitle}</>}
          body={c.pillarsBody}
          pillars={c.pillars}
        />
        <ProductHowItWorks
          eyebrow={c.howEyebrow}
          title={<>{c.howTitle}</>}
          steps={c.steps}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
